import type { ReactNode } from "react";
import { useAuth } from "@/features/auth/useAuth";
import { useShell } from "@/features/shell/useShell";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { useI18n, type TranslationKey } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Search, User } from "lucide-react";
import { ThemeToggle } from "@/components/theme";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

interface ShellHeaderProps {
  children?: ReactNode;
}

export function ShellHeader({ children }: ShellHeaderProps) {
  const { user, logout } = useAuth();
  const { activeShell } = useShell();
  const { t } = useI18n();

  const initials = user
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    : "U";

  const displayName = user
    ? `${user.first_name} ${user.last_name}`.trim() || user.username
    : t("common.user");

  return (
    <header className="dark:bg-background flex h-12 items-center justify-between border-b border-[var(--border)] bg-white px-3 sm:px-7">
      <div className="flex min-w-0 items-center gap-2">
        {children}
        <h2 className="shrink-0 text-[13px] font-medium text-[var(--fg-muted)]">
          {t(`shell.${activeShell}.label` as TranslationKey)}
        </h2>
        {/* Active tenant context: which client and workspace am I working on.
            Hidden on narrow screens, where the breadcrumb bar carries it. */}
        <div className="hidden min-w-0 sm:block">
          <WorkspaceSwitcher />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Search bar (visual, non-functional) */}
        <div className="hidden w-[220px] items-center gap-2 rounded-md border border-transparent bg-[var(--bg-muted)] px-2.5 py-1.5 font-mono text-[12.5px] text-[var(--fg-muted)] sm:flex">
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span>{t("common.searchPlaceholder")}</span>
          <kbd className="ml-auto rounded border border-[var(--border)] bg-[var(--bg-elev)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--fg-muted)]">
            ⌘K
          </kbd>
        </div>
        <LanguageSwitcher />
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-[var(--brand-primary)] text-xs text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm leading-none font-medium">
                  {displayName}
                </p>
                <p className="text-muted-foreground text-xs leading-none">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>{t("common.profile")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t("common.signOut")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
